import {sendEvent} from '@github-ui/hydro-analytics'
import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerPath} from '@github-ui/paths'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {CloudIcon, CopilotIcon, ServerIcon, TerminalIcon, type Icon} from '@primer/octicons-react'
import {Link, RelativeTime} from '@primer/react'
import {SkeletonAvatar, SkeletonText} from '@primer/react/experimental'
import {type ReactNode, useSyncExternalStore} from 'react'
import type {PublicUser} from '../../../types/public-user'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {Session} from '../../../types/session'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {parseDiscussionNumber, parseIssueNumber, parsePullRequestNumber} from '../../../utils/event-identifiers'
import {getChannelOrigin} from '../../../types/origin'
import microsoftTeamsIcon from '../../session-logs/ahp/microsoft-teams.png'
import styles from './SessionMetadataRow.module.css'
import {AGENTS_PATH, CANONICAL_AGENTS_PATH, getAgentsPath} from '../../../utils/constants'

export type SessionEnvironment = 'cli' | 'cloud' | 'environment' | 'sandbox'

const sessionEnvironmentDetails: Record<SessionEnvironment, {icon: Icon; message: string}> = {
  cli: {icon: TerminalIcon, message: 'started a CLI session'},
  cloud: {icon: CloudIcon, message: 'started a cloud session'},
  environment: {icon: ServerIcon, message: 'started an environment session'},
  sandbox: {icon: CloudIcon, message: 'started a cloud session'},
}

const subscribeToOrigin = () => () => {}
const getOriginSnapshot = () => ssrSafeLocation.origin

export interface EventInformationProps {
  session: Session
  threadId?: string
}

export function EventInformation({session, threadId}: EventInformationProps) {
  const origin = useSyncExternalStore(subscribeToOrigin, getOriginSnapshot, getOriginSnapshot)
  const generateOrigin = (
    linkText: string,
    url?: string | null,
    article?: string,
    customPrefix?: string,
    linkLeadingVisual?: ReactNode,
  ) => {
    const prefix = customPrefix ?? (article ? ` from ${article} ` : ' from ')

    return (
      <>
        {prefix}
        {url ? (
          <Link
            href={url}
            inline
            onClick={() => {
              sendEvent('agent_sessions.task_view', {
                target: 'VIEW_SESSION_LINK',
                session_id: session.id,
                task_id: session.resource_global_id,
                thread_id: threadId,
              })
            }}
            className={linkLeadingVisual ? styles.originLink : undefined}
          >
            {linkLeadingVisual}
            {linkText}
          </Link>
        ) : (
          <span>{linkText}</span>
        )}
      </>
    )
  }

  const type = session.event_type
  const identifiers = session.event_identifiers
  const url = session.event_url

  if (threadId) {
    return generateOrigin('conversation', `${origin}${getAgentsPath(`/c/${threadId}`)}`, 'a')
  }

  if (!type) {
    return null
  }

  switch (type) {
    case 'fix_failed_workflow':
    case 'workflow_run_failed':
      return generateOrigin('GitHub Actions', url)
    case 'issues_agent_assignment':
    case 'issue_assigned':
    case 'issue_closed':
    case 'issue_labeled':
    case 'issue_reopened':
    case 'sub_issue_added':
    case 'issue_created': {
      const issueNumber = parseIssueNumber(identifiers)

      return issueNumber ? generateOrigin(`issue #${issueNumber}`, url) : generateOrigin('issue', url, 'an')
    }
    case 'agent_merge':
    case 'pull_request_assigned':
    case 'pull_request_closed':
    case 'pull_request_converted_to_draft':
    case 'pull_request_labeled':
    case 'pull_request_merge_conflict':
    case 'pull_request_reopened':
    case 'pull_request_review_dismissed':
    case 'pull_request_review_requested':
    case 'pull_request_review_submitted':
    case 'pull_request_review_thread_resolved':
    case 'pull_request_review_thread_unresolved':
    case 'pull_request_ready_for_review': {
      const pullRequestNumber = parsePullRequestNumber(identifiers)

      return pullRequestNumber
        ? generateOrigin(`pull request #${pullRequestNumber}`, url)
        : generateOrigin('pull request', url, 'a')
    }
    case 'discussion_opened':
    case 'discussion_updated': {
      const discussionNumber = parseDiscussionNumber(identifiers)

      return discussionNumber
        ? generateOrigin(`discussion #${discussionNumber}`, url)
        : generateOrigin('discussion', url, 'a')
    }
    case 'discussion_comment':
      return generateOrigin('discussion comment', url, 'a')
    case 'pull_request_comment':
    case 'pull_request_comment_non_copilot_pull_request':
      return generateOrigin('pull request comment', url, 'a')
    case 'pull_request_review':
    case 'pull_request_review_non_copilot_pull_request':
      return generateOrigin('pull request review', url, 'a')
    case 'interval_triggered':
      return generateOrigin('scheduled trigger', undefined, 'a')
    case 'merge_conflict':
      return generateOrigin('merge conflict', url, 'a')
    case 'linear_webhook':
      return generateOrigin('Linear', url)
    case 'jira_issue':
      return generateOrigin('Jira', url)
    case 'ios_mobile_mission_control':
    case 'android_mobile_mission_control':
    case 'ios_mobile_repo_profile':
    case 'android_mobile_repo_profile':
      return generateOrigin('GitHub Mobile')
    case 'parent_session': {
      const parentSessionPrefix = 'parent_session::'
      const parentSessionIdentifier = identifiers?.find(id => id.startsWith(parentSessionPrefix))
      const parentSessionName = parentSessionIdentifier?.slice(parentSessionPrefix.length)
      return parentSessionName ? generateOrigin(parentSessionName, url) : null
    }
    case 'legacy_chat': {
      const legacyChatPrefix = 'legacy_chat::'
      const legacyChatUrlPrefix = 'legacy_chat_url::'
      const legacyChatIdentifier = identifiers?.find(id => id.startsWith(legacyChatPrefix))
      const legacyChatName = legacyChatIdentifier?.slice(legacyChatPrefix.length)
      const legacyChatUrlIdentifier = identifiers
        ?.find(id => id.startsWith(legacyChatUrlPrefix))
        ?.slice(legacyChatUrlPrefix.length)
      let legacyChatUrl = url
      if (!legacyChatUrl && legacyChatUrlIdentifier) {
        try {
          const parsedUrl = new URL(legacyChatUrlIdentifier, origin)
          if (
            parsedUrl.origin === origin &&
            [AGENTS_PATH, CANONICAL_AGENTS_PATH].some(path => parsedUrl.pathname.startsWith(`${path}/c/`))
          ) {
            legacyChatUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
          }
        } catch {
          legacyChatUrl = null
        }
      }
      return legacyChatName
        ? generateOrigin(legacyChatName, legacyChatUrl, undefined, ' from the legacy chat ')
        : generateOrigin('legacy chat', legacyChatUrl, 'a')
    }
    case 'slack':
    case 'teams': {
      const channelOrigin = getChannelOrigin(type)
      if (!channelOrigin) return null

      const channelIcon =
        type === 'slack' ? (
          <span data-testid="slack-channel-icon" className={styles.originIcon}>
            <SlackIcon />
          </span>
        ) : (
          <span data-testid="teams-channel-icon" className={styles.originIcon}>
            <img src={microsoftTeamsIcon} alt="" aria-hidden="true" />
          </span>
        )

      return generateOrigin(channelOrigin, url, undefined, ' via ', channelIcon)
    }
    default:
      return null
  }
}

export interface SessionMetadataRowProps {
  currentUser?: PublicUser
  threadId?: string
  session: Session
  environment: SessionEnvironment
  message?: string
  agentInfo?: AgentInfo | null
  isAgentInfoLoading?: boolean
  isTaskCreatedByAutomation?: boolean
  automationDisplayName?: string
}

export function SessionMetadataRow({
  currentUser,
  threadId,
  session,
  environment,
  message,
  agentInfo,
  isAgentInfoLoading = false,
  isTaskCreatedByAutomation,
  automationDisplayName,
}: SessionMetadataRowProps) {
  const isCurrentUserTheCreator = !isTaskCreatedByAutomation && currentUser?.id === session.user_id
  const creatorLogin = isTaskCreatedByAutomation
    ? undefined
    : session.user_login || (isCurrentUserTheCreator ? currentUser?.login : undefined)
  const metadataDisplayName = isTaskCreatedByAutomation ? automationDisplayName || 'Automation' : creatorLogin || 'User'
  const agentDisplayName = agentInfo?.display_name ?? 'Agent'
  const isCopilotAgent = isCopilotCodingAgent(agentInfo?.slug)
  const {icon: EnvironmentIcon, message: environmentMessage} = sessionEnvironmentDetails[environment]

  return (
    <div className={styles.metadata} data-testid="session-metadata-row">
      <EnvironmentIcon
        size={16}
        className={styles.metadataIcon}
        aria-hidden="true"
        data-testid={`session-environment-icon-${environment}`}
      />
      <div className={styles.metadataDetails}>
        <span>
          {creatorLogin ? (
            <Link className={styles.creatorLink} href={ownerPath({owner: creatorLogin})} inline>
              {creatorLogin}
            </Link>
          ) : (
            <span>{metadataDisplayName}</span>
          )}{' '}
          <span>{message ?? environmentMessage}</span>
          <EventInformation session={session} threadId={threadId} />
        </span>
        <RelativeTime date={new Date(session.created_at)} tense="past" />
        <span aria-hidden="true">·</span>
        {isAgentInfoLoading ? (
          <span className={styles.agentSkeleton} role="status" aria-label="Loading agent">
            <span className={styles.agentVisual} aria-hidden="true">
              <SkeletonAvatar size={16} />
            </span>
            <span className={styles.agentNameSkeleton} aria-hidden="true">
              <SkeletonText size="bodySmall" />
            </span>
          </span>
        ) : (
          <span className={styles.agentDetails} data-testid="agent-details">
            <span className={styles.agentVisual} aria-hidden="true">
              {isCopilotAgent || !agentInfo?.avatar_url ? (
                <CopilotIcon size={16} />
              ) : (
                <GitHubAvatar src={agentInfo.avatar_url} size={16} square />
              )}
            </span>
            <span>{agentDisplayName}</span>
          </span>
        )}
      </div>
    </div>
  )
}
